import { del, get, patch, post } from './api';
import type { ClientUser } from '@/store/auth.store';

export type EstablishmentTheme = {
  primary?: string | null;
  fontFamily?: string | null;
  faviconUrl?: string | null;
  iconUrl?: string | null;
};

export type Establishment = {
  id: string;
  name: string;
  logoUrl: string | null;
  address: string | null;
  phone: string | null;
  theme?: EstablishmentTheme | null;
};

export const listEstablishments = async (): Promise<Establishment[]> => {
  const res = await get<Establishment[] | { results: Establishment[] }>(
    '/api/client/establishments',
  );
  return Array.isArray(res) ? res : res.results ?? [];
};

// Резолвит заведение по hostname (для мультидоменного деплоя меню).
// Возвращает null если для домена нет привязки.
export const getEstablishmentByHost = async (host: string) => {
  try {
    return await get<Establishment | null>(
      `/api/client/establishments/by-host?host=${encodeURIComponent(host)}`,
    );
  } catch {
    return null;
  }
};

export type MenuResponse = {
  establishment: { id: string; name: string; logoUrl: string | null };
  categories: Array<{
    id: string;
    name: string;
    dishes: Array<{
      id: string;
      name: string;
      description: string | null;
      price: number;
      photoUrl: string | null;
      photos?: string[];
      weight: string | null;
      modifiers: Array<{ id: string; name: string; price: number }>;
    }>;
  }>;
};
export const getMenu = (estId: string) =>
  get<MenuResponse>(`/api/client/establishments/${estId}/menu`);

// ── Tables (для выбора столика в зале) ─────────────
export type HallTable = {
  id: string;
  number: number;
  seats: number;
  area: 'HALL' | 'TAKEAWAY';
  is_free: boolean;
};
export const getHallTables = async (estId: string): Promise<HallTable[]> => {
  const res = await get<{ results: HallTable[] }>(
    `/api/client/establishments/${estId}/tables`,
  );
  return res.results ?? [];
};

// ── Auth (SMS) ────────────────
// establishmentId нужен, чтобы клиент регался «в рамках» кафе —
// иначе один и тот же email/phone в разных заведениях сливался бы в одного user.
export const sendCode = (phone: string, establishmentId?: string | null) =>
  post('/api/auth/client/send-code', { phone, establishment_id: establishmentId ?? undefined });
export const verifyCode = (
  phone: string,
  code: string,
  name?: string,
  establishmentId?: string | null,
) =>
  post<{ access_token: string; refresh_token: string; user: ClientUser }>(
    '/api/auth/client/verify-code',
    { phone, code, name, establishment_id: establishmentId ?? undefined },
  );

// ── Auth (Email OTP) ──────────
export const sendEmailCode = (email: string, establishmentId?: string | null) =>
  post('/api/auth/client/email/send-code', { email, establishment_id: establishmentId ?? undefined });
export const verifyEmailCode = (
  email: string,
  code: string,
  name?: string,
  establishmentId?: string | null,
) =>
  post<{ access_token: string; refresh_token: string; user: ClientUser }>(
    '/api/auth/client/email/verify-code',
    { email, code, name, establishment_id: establishmentId ?? undefined },
  );

// ── Profile ───────────────────
export type SavedCard = {
  id: string;
  last4: string;
  brand: string;
  isDefault: boolean;
};
export type ClientMe = ClientUser & {
  addresses: Address[];
  cards: SavedCard[];
};

// Лёгкий профиль (name, phone, email) — используется BottomNav/общим UI.
export const getProfile = () => get<{ user: ClientUser }>('/api/auth/me');
// Полный профиль клиента с адресами и картами.
export const getClientMe = () => get<{ user: ClientMe }>('/api/client/me');
// Бэк принимает только имя (телефон привязан к SMS-авторизации, email не редактируется).
export const updateProfile = (b: { name?: string | null }) =>
  patch<{ id: string; name: string | null }>('/api/client/me', b);
export const deleteProfile = () => del('/api/client/me');

// ── Addresses ─────────────────
export type Address = {
  id: string; title: string; address: string;
  entrance?: string | null; floor?: string | null; apartment?: string | null;
  comment?: string | null; isDefault: boolean;
};
export const listAddresses = async (): Promise<Address[]> => {
  const res = await get<Address[] | { results: Address[] }>('/api/client/addresses');
  return Array.isArray(res) ? res : res.results ?? [];
};
export const addAddress = (b: Partial<Address>) => post<Address>('/api/client/addresses', b);
export const updateAddress = (id: string, b: Partial<Address>) =>
  patch<Address>(`/api/client/addresses/${id}`, b);
export const removeAddress = (id: string) => del(`/api/client/addresses/${id}`);

// ── Orders ────────────────────
export type CreateOrderPayload = {
  establishmentId: string;
  items: Array<{
    dish_id: string;
    quantity: number;
    modifiers?: Array<{ id?: string; name: string; price?: number }>;
    comment?: string;
  }>;
  comment?: string;
  delivery_address?: string;
  phone?: string;
  order_type: 'hall' | 'takeaway';
  table_id?: string;
  payment: 'online' | 'cash';
  return_url?: string;
};

export type ClientOrder = {
  id: string;
  number: number;
  status: string;
  total: number;
  createdAt: string;
  items: Array<{ id: string; name: string; quantity: number; total: number }>;
};

export type CreateOrderResponse = {
  order: ClientOrder;
  confirmationUrl?: string | null;
};

// Бэк (cashier/orders.service.ts → orderToDto) отдаёт snake_case и lowercase-статус.
// Нормализуем под типы фронта, чтобы не плодить правки по страницам.
type RawOrder = {
  id: string;
  number: number;
  status: string;
  total: number;
  created_at?: string;
  createdAt?: string;
  items?: Array<{ id: string; name: string; quantity: number; total: number }>;
};

function normalizeOrder(r: RawOrder): ClientOrder {
  return {
    id: r.id,
    number: r.number,
    status: (r.status ?? '').toUpperCase(),
    total: r.total,
    createdAt: r.created_at ?? r.createdAt ?? new Date().toISOString(),
    items: Array.isArray(r.items) ? r.items : [],
  };
}

export const createOrder = async (b: CreateOrderPayload): Promise<CreateOrderResponse> => {
  const res = await post<
    | { order: RawOrder; confirmation_url?: string | null }
    | RawOrder
  >('/api/client/orders', b);
  if ('order' in res && res.order) {
    return { order: normalizeOrder(res.order), confirmationUrl: res.confirmation_url ?? null };
  }
  return { order: normalizeOrder(res as RawOrder), confirmationUrl: null };
};

export const listMyOrders = async (): Promise<ClientOrder[]> => {
  const res = await get<RawOrder[] | { results: RawOrder[] }>('/api/client/orders');
  const list = Array.isArray(res) ? res : res.results ?? [];
  return list.map(normalizeOrder);
};

export const getOrder = async (id: string): Promise<ClientOrder> => {
  const res = await get<RawOrder>(`/api/client/orders/${id}`);
  return normalizeOrder(res);
};

// Активный заказ клиента в конкретном заведении (если есть). Нужен Checkout'у,
// чтобы предложить «добавить к моему столу» вместо создания нового заказа.
export type ActiveOrder = {
  id: string;
  number: number;
  status: string;
  total: number;
  orderType?: 'HALL' | 'TAKEAWAY' | string;
  tableNumber?: number | null;
  tableArea?: 'HALL' | 'TAKEAWAY' | string | null;
};
// Бэкенд в orderToDto отдаёт table_number + zone ('hall' | 'takeaway'). Типы
// покрывают оба варианта — на случай, если где-то в DTO появится вложенный table.
type RawActiveOrder = RawOrder & {
  table?: { id: string; number: number; area: string } | null;
  table_number?: number | string | null;
  zone?: string | null;
  order_type?: string;
};
export const getActiveOrder = async (estId: string): Promise<ActiveOrder | null> => {
  const res = await get<{ order: RawActiveOrder | null }>(
    `/api/client/orders/active?establishmentId=${encodeURIComponent(estId)}`,
  );
  const o = res.order;
  if (!o) return null;
  const zone = o.zone ?? o.table?.area ?? null;
  const tableArea = zone ? zone.toUpperCase() : null;
  const tableNumber =
    o.table?.number ?? (o.table_number != null ? Number(o.table_number) : null);
  return {
    id: o.id,
    number: o.number,
    status: (o.status ?? '').toUpperCase(),
    total: o.total,
    orderType: (o.order_type ?? tableArea ?? undefined) as ActiveOrder['orderType'],
    tableNumber,
    tableArea,
  };
};

// Добавить позиции к уже существующему заказу. Возвращает обновлённый заказ.
export const appendOrderItems = async (
  orderId: string,
  items: CreateOrderPayload['items'],
): Promise<ClientOrder> => {
  const res = await post<{ order: RawOrder }>(
    `/api/client/orders/${orderId}/items`,
    { items },
  );
  return normalizeOrder(res.order);
};
